import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { CheckCircle2, ChevronRight, ChevronLeft, FileText, Users, Target, ListChecks, Settings, Mail, Eye, Rocket, Plus, Trash2, Search, FileEdit, Layers, AlertTriangle } from 'lucide-react';
import { INITIAL_COMPETENCIES, LEVELS, appliesToLevel, isUniversal } from '../data/competencies';
import type { Level } from '../data/competencies';
const STEPS = [{
  id: 1,
  title: 'Assessment Details',
  icon: FileText
}, {
  id: 2,
  title: 'Assessment Type',
  icon: Users
}, {
  id: 3,
  title: 'Select Competencies',
  icon: Target
}, {
  id: 4,
  title: 'Question Templates',
  icon: ListChecks
}, {
  id: 5,
  title: 'Customize Questions',
  icon: FileText
}, {
  id: 6,
  title: 'Rating Scale',
  icon: Settings
}, {
  id: 7,
  title: 'Invite Respondents',
  icon: Mail
}, {
  id: 8,
  title: 'Review',
  icon: Eye
}, {
  id: 9,
  title: 'Launch',
  icon: Rocket
}];
export function CreateAssessment() {
  const initial = useScreenInit();
  const [currentStep, setCurrentStep] = useState(typeof initial.step === 'number' ? initial.step : 1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: '',
    type: '360',
    level: 'Manager' as Level,
    competencies: [] as string[],
    template: 'standard',
    scale: '5',
    respondents: [] as string[]
  });
  const [showAllCompetencies, setShowAllCompetencies] = useState(false);
  const activeCompetencies = INITIAL_COMPETENCIES.filter(c => !c.archived);
  const matchingCompetencies = activeCompetencies.filter(c => appliesToLevel(c, formData.level));
  const visibleCompetencies = showAllCompetencies ? activeCompetencies : matchingCompetencies;
  // Selections kept from a previous level are flagged, never silently dropped.
  const offLevelSelected = formData.competencies.filter(name => {
    const match = activeCompetencies.find(c => c.name === name);
    return match ? !appliesToLevel(match, formData.level) : false;
  });
  const handleNext = () => {
    if (currentStep < 9) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-901" data-mp="1tosuafh2c3ua">
            <div data-id="element-902" data-mp="1tqnic2nmsc2l">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-903" data-mp="1opg9e09r56uc">
                Assessment Details
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-904" data-mp="1jqwnu0z2lx2i">
                Provide the basic information for this assessment cycle.
              </p>
            </div>
            <div className="space-y-4" data-id="element-905" data-mp="3j14pi5rpzfgb">
              <div data-id="element-906" data-mp="3e44kly6lwiqs">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-907" data-mp="3e5pbndtppjjw">
                  Assessment Name *
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g., Q3 Leadership Review" className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-908" data-mp="3qn08032rm1pp" />
              </div>
              <div data-id="element-909" data-mp="3clnrnqjozy3a">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-910" data-mp="3cpbniwcm0wl6">
                  Description
                </label>
                <textarea rows={3} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Brief description of the assessment goals..." className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-911" data-mp="rxj8j6h1i1a1" />
              </div>
              <div data-id="element-912" data-mp="1dg1xpy9uas2j">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-913" data-mp="1dfiduc0mptlv">
                  Due Date *
                </label>
                <input type="date" value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-914" data-mp="19egd1qr2d9hs" />
              </div>
            </div>
          </div>;
      case 2:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-915" data-mp="2fra4ccbenbzf">
            <div data-id="element-916" data-mp="21tbqr94wrjkz">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-917" data-mp="21rqxdmuby6op">
                Assessment Type
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-918" data-mp="26qbwfixuikbk">
                Select the feedback structure for this cycle.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-919" data-mp="1wth3rgo97e57">
              {[{
              id: 'self',
              title: 'Self-Assessment',
              desc: 'Individuals evaluate their own performance.'
            }, {
              id: '180',
              title: '180° Feedback',
              desc: 'Manager and self-evaluation combined.'
            }, {
              id: '360',
              title: '360° Feedback',
              desc: 'Comprehensive feedback from peers, managers, and direct reports.'
            }].map(type => <div key={type.id} onClick={() => setFormData({
              ...formData,
              type: type.id
            })} className={`p-4 border rounded-xl cursor-pointer transition-all ${formData.type === type.id ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-920" data-mp="q61ki1qw0ts0">
                  <div className="flex justify-between items-start mb-2" data-id="element-921" data-mp="141dshau20gqv">
                    <h3 className="font-medium text-slate-900" data-id="element-922" data-mp="yzeobyqhpfsv">{type.title}</h3>
                    {formData.type === type.id && <CheckCircle2 className="h-5 w-5 text-slate-900" data-id="element-923" data-mp="3on5jwq15cotv" />}
                  </div>
                  <p className="text-sm text-slate-500" data-id="element-924" data-mp="1z8eb1yst0ccz">{type.desc}</p>
                </div>)}
            </div>

            <div className="border-t border-slate-200 pt-6" data-id="element-925" data-mp="246ppynxyhd22">
              <label htmlFor="assessment-level" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-926" data-mp="2454w70jgty59">
                Level being assessed *
              </label>
              <p className="text-sm text-slate-500 mb-3" data-id="element-927" data-mp="2iwnfb77mg8su">
                We'll use this to recommend the competencies that apply at this
                level.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-928" data-mp="2mztmch2k640d">
                {LEVELS.map(level => {
                const selected = formData.level === level;
                return <button key={level} type="button" onClick={() => setFormData({
                  ...formData,
                  level
                })} aria-pressed={selected} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${selected ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-929" data-mp="2wu2a6rgn98s7">
                      {level}
                    </button>;
              })}
              </div>
            </div>
          </div>;
      case 3:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-930" data-mp="2gj9w1phk8c5b">
            <div data-id="element-931" data-mp="2vf99zgrcp6id">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-932" data-mp="2vfr8yl5edytz">
                Select Competencies
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-933" data-mp="2qhzebjddbym3">
                Choose the key areas to evaluate.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-violet-50 border border-violet-100 rounded-lg px-4 py-3" data-id="element-934" data-mp="5q7406sqtrha">
              <div className="flex items-center text-sm text-slate-700" data-id="element-935" data-mp="db9ivc04m4cr">
                <Layers className="h-4 w-4 text-violet-500 mr-2 shrink-0" data-id="element-936" data-mp="i92r0akzp8fo" />
                <span data-id="element-937" data-mp="49a28kt0x2ac">
                  Showing{' '}
                  <span className="font-medium text-slate-900" data-id="element-938" data-mp="4auvm73lqf6m">
                    {visibleCompetencies.length}
                  </span>{' '}
                  {visibleCompetencies.length === 1 ? 'competency' : 'competencies'}{' '}
                  for{' '}
                  <span className="font-medium text-slate-900" data-id="element-939" data-mp="110lqi7ft24cy">
                    {formData.level}
                  </span>
                </span>
              </div>
              <button type="button" onClick={() => setShowAllCompetencies(!showAllCompetencies)} aria-pressed={showAllCompetencies} className="text-sm font-medium text-violet-700 hover:text-violet-900 shrink-0 text-left sm:text-right" data-id="element-940" data-mp="r7engt8jf0g1">
                {showAllCompetencies ? `Show only ${formData.level}` : 'Show all competencies'}
              </button>
            </div>

            {offLevelSelected.length > 0 && <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-941" data-mp="2hxg74oc1v3q8">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" data-id="element-942" data-mp="2931wiz2gshdg" />
                <p className="text-sm text-amber-800" data-id="element-943" data-mp="290xi3n6xvzyx">
                  {offLevelSelected.length}{' '}
                  {offLevelSelected.length === 1 ? 'selected competency does' : 'selected competencies do'}{' '}
                  not apply to {formData.level}. They'll stay selected unless
                  you remove them.
                </p>
              </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-944" data-mp="1u6138r4mkecv">
              {visibleCompetencies.map(comp => {
              const isSelected = formData.competencies.includes(comp.name);
              const offLevel = !appliesToLevel(comp, formData.level);
              return <button key={comp.id} type="button" onClick={() => {
                if (isSelected) {
                  setFormData({
                    ...formData,
                    competencies: formData.competencies.filter(c => c !== comp.name)
                  });
                } else {
                  setFormData({
                    ...formData,
                    competencies: [...formData.competencies, comp.name]
                  });
                }
              }} aria-pressed={isSelected} className={`flex items-start p-3 border rounded-lg text-left transition-colors ${isSelected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-945" data-mp="1khncewoxm5n4">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 mt-0.5 shrink-0 ${isSelected ? 'bg-slate-900 border-slate-900' : 'border-slate-300'}`} data-id="element-946" data-mp="19w6rzqr3bclh">
                      {isSelected && <CheckCircle2 className="h-3 w-3 text-white" data-id="element-947" data-mp="1olcpnaaa4ur9" />}
                    </div>
                    <span className="min-w-0" data-id="element-948" data-mp="1tibgnjyncll8">
                      <span className="block text-sm font-medium text-slate-900" data-id="element-949" data-mp="1xl85vtiozfy5">
                        {comp.name}
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5" data-id="element-950" data-mp="22j9ka156ar3a">
                        {comp.category}
                      </span>
                      {offLevel ? <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-951" data-mp="85i6qqjq3n2k">
                          Outside {formData.level}
                        </span> : isUniversal(comp) && <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-500 border border-slate-200" data-id="element-952" data-mp="3n351htc6yi7l">
                            All levels
                          </span>}
                    </span>
                  </button>;
            })}
            </div>

            {visibleCompetencies.length === 0 && <div className="border border-dashed border-slate-300 rounded-lg py-10 text-center" data-id="element-953" data-mp="9pjrig1khck8">
                <p className="text-sm text-slate-500" data-id="element-954" data-mp="5ny80buj2012">
                  No competencies are mapped to {formData.level} yet.
                </p>
                <button type="button" onClick={() => setShowAllCompetencies(true)} className="mt-3 text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-955" data-mp="2qbupk06icdyf">
                  Show all competencies
                </button>
              </div>}
          </div>;
      case 4:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-956" data-mp="d5347omj676u">
            <div data-id="element-957" data-mp="10tmfmd0vn035">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-958" data-mp="10v7ak3r3sl1j">
                Question Templates
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-959" data-mp="15s7gmenydnsn">
                Start with a pre-built set of questions or create your own.
              </p>
            </div>
            <div className="space-y-3" data-id="element-960" data-mp="vtrp4al0kcim">
              {[{
              id: 'standard',
              title: 'Standard Leadership Template',
              desc: '24 questions covering core leadership competencies.'
            }, {
              id: 'agile',
              title: 'Agile Team Template',
              desc: '15 questions focused on adaptability and execution.'
            }, {
              id: 'custom',
              title: 'Start from Scratch',
              desc: 'Build your own questionnaire from our library.'
            }].map(tpl => <div key={tpl.id} onClick={() => setFormData({
              ...formData,
              template: tpl.id
            })} className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.template === tpl.id ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-961" data-mp="2qahzs79g7duc">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-4 shrink-0 ${formData.template === tpl.id ? 'border-slate-900' : 'border-slate-300'}`} data-id="element-962" data-mp="22m8gpd3md46w">
                    {formData.template === tpl.id && <div className="w-2.5 h-2.5 rounded-full bg-slate-900" data-id="element-963" data-mp="311e7l0hz7du8" />}
                  </div>
                  <div data-id="element-964" data-mp="35zxwn9rv95qt">
                    <h3 className="font-medium text-slate-900" data-id="element-965" data-mp="3607mmxcwvcwi">{tpl.title}</h3>
                    <p className="text-sm text-slate-500" data-id="element-966" data-mp="29c1nur2o7eq5">{tpl.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-967" data-mp="2ian4uhshulot">
            <div className="flex justify-between items-center" data-id="element-968" data-mp="2nb13584amh9n">
              <div data-id="element-969" data-mp="2n8539e8vxiik">
                <h2 className="text-xl font-semibold text-slate-900" data-id="element-970" data-mp="3tqtsqj3cun3v">
                  Customize Questions
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-971" data-mp="2j7n4kx2sakd">
                  Review and edit the questions for this assessment.
                </p>
              </div>
              <button className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50 flex items-center" data-id="element-972" data-mp="1i3hzxn65n14k">
                <Plus className="h-4 w-4 mr-1" data-id="element-973" data-mp="1daou0vyy9mqw" /> Add Question
              </button>
            </div>
            <div className="space-y-4" data-id="element-974" data-mp="18d55924mlrkp">
              {[{
              q: 'Effectively communicates the strategic vision to the team.',
              comp: 'Communication'
            }, {
              q: 'Demonstrates ability to adapt to changing circumstances.',
              comp: 'Adaptability'
            }, {
              q: 'Provides constructive and timely feedback to direct reports.',
              comp: 'Team Leadership'
            }].map((item, i) => <div key={i} className="p-4 border border-slate-200 rounded-lg bg-white flex items-start justify-between group" data-id="element-975" data-mp="2jtvgx0rtjblp" data-mpi={i}>
                  <div data-id="element-976" data-mp="1xmkkqre7qvng">
                    <div className="text-xs font-medium text-slate-500 mb-1 uppercase tracking-wider" data-id="element-977" data-mp="1xlt0gcq2gkch">
                      {item.comp}
                    </div>
                    <div className="text-sm text-slate-900 font-medium" data-id="element-978" data-mp="1nvzm53yjhr4j">
                      {item.q}
                    </div>
                  </div>
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-979" data-mp="19yt8fnsoaim6">
                    <button className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-980" data-mp="1etqfip6e6hwf">
                      <FileEdit className="h-4 w-4" data-id="element-981" data-mp="14xndrkfgcla0" />
                    </button>
                    <button className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50" data-id="element-982" data-mp="14zq3e3cp0ce7">
                      <Trash2 className="h-4 w-4" data-id="element-983" data-mp="2v88ipgqbhm13" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>;
      case 6:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-984" data-mp="jmpqbid4zo1c">
            <div data-id="element-985" data-mp="jkmtaedkgun4">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-986" data-mp="xe4ctz6967d5">
                Rating Scale
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-987" data-mp="snnymculyhpq">
                Define how respondents will rate the questions.
              </p>
            </div>
            <div className="space-y-4" data-id="element-988" data-mp="17ah6wy5pmn8f">
              <div data-id="element-989" data-mp="12dh898b6wmrc">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-990" data-mp="12e0kc8e5sgxg">
                  Scale Type
                </label>
                <select value={formData.scale} onChange={e => setFormData({
                ...formData,
                scale: e.target.value
              })} className="w-full md:w-1/2 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm bg-white" data-id="element-991" data-mp="1gc76wsdnquht">
                  <option value="5" data-id="element-992" data-mp="2ggndooru5t0z">5-Point Scale</option>
                  <option value="7" data-id="element-993" data-mp="2u93l01rrsd7p">7-Point Scale</option>
                  <option value="10" data-id="element-994" data-mp="2u8lkuu1zleui">10-Point Scale</option>
                </select>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-995" data-mp="2q8bdpe2gkie2">
                <h4 className="text-sm font-medium text-slate-900 mb-3" data-id="element-996" data-mp="22iqi9vp4bsnw">
                  Preview (5-Point)
                </h4>
                <div className="flex justify-between items-center text-sm" data-id="element-997" data-mp="1xkxflcn0xgsd">
                  <div className="text-center" data-id="element-998" data-mp="2bk6r3z0abmq0">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium" data-id="element-999" data-mp="26jsh4bf4i2pc">
                      1
                    </div>
                    <span className="text-xs text-slate-500" data-id="element-1000" data-mp="3hypj0lweome4">
                      Strongly Disagree
                    </span>
                  </div>
                  <div className="text-center" data-id="element-1001" data-mp="3qe52d9e612qb">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium" data-id="element-1002" data-mp="3uh1qffmh5r1n">
                      2
                    </div>
                  </div>
                  <div className="text-center" data-id="element-1003" data-mp="3lg3k2qokd7fw">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium" data-id="element-1004" data-mp="3lgvg22m2p76p">
                      3
                    </div>
                    <span className="text-xs text-slate-500" data-id="element-1005" data-mp="hz3hu6hcfr02">Neutral</span>
                  </div>
                  <div className="text-center" data-id="element-1006" data-mp="3ah1c1fzct79">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium" data-id="element-1007" data-mp="89tn0iv47u89">
                      4
                    </div>
                  </div>
                  <div className="text-center" data-id="element-1008" data-mp="10qnwe4kn5yuh">
                    <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium" data-id="element-1009" data-mp="10sh7bw2af58a">
                      5
                    </div>
                    <span className="text-xs text-slate-500" data-id="element-1010" data-mp="15o364x7iv5fx">
                      Strongly Agree
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 7:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1011" data-mp="rok7cv0lp6ga">
            <div data-id="element-1012" data-mp="mx26s82hp7vu">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-1013" data-mp="mwim4jlgg5e4">
                Invite Respondents
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1014" data-mp="hyh5s6feyk6c">
                Select the subjects who will be evaluated in this cycle.
              </p>
            </div>
            <div className="relative" data-id="element-1015" data-mp="8xiz1gdlbyk2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1016" data-mp="8whodd0hle2v" />
              <input type="text" placeholder="Search users or departments..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-1017" data-mp="3yze9jaqgsta" />
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-1018" data-mp="2omr71epgx3sp">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1019" data-mp="2tjrhs2x9iuq5">
                Suggested Users
              </div>
              <div className="divide-y divide-slate-100" data-id="element-1020" data-mp="20x6ogwx888jy">
                {['Sarah Connor', 'Michael Scott', 'Jim Halpert'].map(name => <div key={name} className="flex items-center justify-between p-3 hover:bg-slate-50" data-id="element-1021" data-mp="2alwzeya8v6v3">
                      <div className="flex items-center" data-id="element-1022" data-mp="1d4q5mk5o0cjl">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3" data-id="element-1023" data-mp="1i4udxmwf60yq">
                          {name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <span className="text-sm font-medium text-slate-900" data-id="element-1024" data-mp="3tmza6urj4lzh">
                          {name}
                        </span>
                      </div>
                      <button className="text-sm text-slate-600 hover:text-slate-900 font-medium px-3 py-1 border border-slate-200 rounded hover:bg-white" data-id="element-1025" data-mp="7cd38mfrs9x2">
                        Add
                      </button>
                    </div>)}
              </div>
            </div>
          </div>;
      case 8:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1026" data-mp="2i5662jrd8rgg">
            <div data-id="element-1027" data-mp="2i7ixromxeg6p">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-1028" data-mp="2e4dyduy56uma">
                Review Assessment
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1029" data-mp="23zxix2okrfj0">
                Please review the details before launching.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-6" data-id="element-1030" data-mp="283lr9nl16f5b">
              <div className="grid grid-cols-2 gap-4" data-id="element-1031" data-mp="1u89vrdz1wknc">
                <div data-id="element-1032" data-mp="1z8fgsbatf8wg">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1033" data-mp="1z7wbpv69kezu">
                    Name
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-1034" data-mp="1pb9sfatl5vzx">
                    {formData.name || 'Untitled Assessment'}
                  </div>
                </div>
                <div data-id="element-1035" data-mp="1bdcqdywd4xdv">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1036" data-mp="1bduu1glcudvt">
                    Due Date
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-1037" data-mp="35qkru9eug78h">
                    {formData.dueDate || 'Not set'}
                  </div>
                </div>
                <div data-id="element-1038" data-mp="389z9jwvr3cbf">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1039" data-mp="38ah5esei3oit">
                    Type
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-1040" data-mp="346swo6e4umvz">
                    {formData.type}° Feedback
                  </div>
                </div>
                <div data-id="element-1041" data-mp="2uapu4zfdcm8i">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1042" data-mp="2ub7xsh4d22qg">
                    Scale
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-1043" data-mp="3s018dm7wq2ix">
                    {formData.scale}-Point
                  </div>
                </div>
                <div data-id="element-1044" data-mp="3i7ddix0hm8mu">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1045" data-mp="3i6kg4mnf4vhd">
                    Level Assessed
                  </div>
                  <div className="font-medium text-slate-900" data-id="element-1046" data-mp="ej21hg7e1fvi">
                    {formData.level}
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4" data-id="element-1047" data-mp="9r0sc35io59p">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2" data-id="element-1048" data-mp="h49mqa3m2p28">
                  Competencies ({formData.competencies.length})
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-1049" data-mp="r1xm5dc5wkqq">
                  {formData.competencies.length > 0 ? formData.competencies.map(c => {
                  const isOffLevel = offLevelSelected.includes(c);
                  return <span key={c} className={`px-2 py-1 border rounded text-xs font-medium ${isOffLevel ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-slate-200 text-slate-700'}`} data-id="element-1050" data-mp="1009i7uvssaqk">
                          {c}
                          {isOffLevel && ' •'}
                        </span>;
                }) : <span className="text-sm text-slate-500" data-id="element-1051" data-mp="1nsygo7av9q5t">
                      None selected
                    </span>}
                </div>
                {offLevelSelected.length > 0 && <div className="flex items-start gap-2 mt-3" data-id="element-1052" data-mp="250qjw58fzd2e">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" data-id="element-1053" data-mp="2j1b5181txkz2" />
                    <p className="text-xs text-amber-800" data-id="element-1054" data-mp="2e2815qxl5kso">
                      {offLevelSelected.length}{' '}
                      {offLevelSelected.length === 1 ? 'competency falls' : 'competencies fall'}{' '}
                      outside the {formData.level} level.
                    </p>
                  </div>}
              </div>
            </div>
          </div>;
      case 9:
        return <div className="py-12 text-center animate-in fade-in zoom-in duration-500" data-id="element-1055" data-mp="1v9njz6r9144p">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-1056" data-mp="17jt7iqaudhkz">
              <Rocket className="h-8 w-8 text-emerald-600" data-id="element-1057" data-mp="3ea4yxom50ozi" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1058" data-mp="3e8ts1fxeb146">
              Assessment Launched!
            </h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8" data-id="element-1059" data-mp="359gk4rox6klk">
              Your assessment cycle has been successfully created and
              invitations have been sent to the selected respondents.
            </p>
            <div className="flex justify-center space-x-4" data-id="element-1060" data-mp="uqjgzcmz55q">
              <Link to="/app/assessments" className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1061" data-mp="t5m71yxt846">
                View Assessments
              </Link>
              <Link to="/app" className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-1062" data-mp="3sxpyx9npu837">
                Go to Dashboard
              </Link>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="max-w-5xl mx-auto animate-in fade-in duration-500" data-id="element-1063" data-mp="1fp03ilmg5sqe">
      <div className="mb-8" data-id="element-1064" data-mp="1kk5s5s78hli6">
        <Link to="/app/assessments" className="text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center mb-4" data-id="element-1065" data-mp="1pktqz01pf6my">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1066" data-mp="1tl3t21mdlcwj" /> Back to Assessments
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1067" data-mp="wx7air7do7io">
          Create New Assessment
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row min-h-[600px]" data-id="element-1068" data-mp="11vqxmuxpjpcm">
        {/* Sidebar Progress */}
        <div className="w-full md:w-64 bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 p-6 shrink-0 hidden md:block" data-id="element-1069" data-mp="1ansvqmkb0tjq">
          <nav className="space-y-1 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent hidden" data-id="element-1070" data-mp="e5eip041oscj">
            {/* Keeping the line hidden for a cleaner look, using active states instead */}
          </nav>
          <div className="space-y-2" data-id="element-1071" data-mp="1lq10tq46ccd">
            {STEPS.map(step => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isPast = currentStep > step.id;
            return <div key={step.id} className={`flex items-center p-2 rounded-lg transition-colors ${isActive ? 'bg-white shadow-sm border border-slate-200' : 'border border-transparent'}`} data-id="element-1072" data-mp="3j3iy5zrjspvu">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 shrink-0 ${isActive ? 'bg-slate-900 text-white' : isPast ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-400'}`} data-id="element-1073" data-mp="3a3vx5f2qdm3b">
                    {isPast ? <CheckCircle2 className="h-5 w-5" data-id="element-1074" data-mp="2qg0zv0zyovgj" /> : <Icon className="h-4 w-4" data-id="element-1075" data-mp="2vgfb0rwv0lis" />}
                  </div>
                  <span className={`text-sm font-medium ${isActive ? 'text-slate-900' : isPast ? 'text-slate-700' : 'text-slate-400'}`} data-id="element-1076" data-mp="2gjeg6fjdgag3">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Mobile Progress Bar */}
        <div className="md:hidden p-4 border-b border-slate-200 bg-slate-50" data-id="element-1077" data-mp="3e5vb5e4mubyo">
          <div className="flex items-center justify-between text-sm font-medium text-slate-700 mb-2" data-id="element-1078" data-mp="2zgz8xxp9bt5z">
            <span data-id="element-1079" data-mp="34f9448azetrl">
              Step {currentStep} of {STEPS.length}
            </span>
            <span data-id="element-1080" data-mp="3s5mmf73rle8z">{Math.round(currentStep / STEPS.length * 100)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-1081" data-mp="ssb209lfj1p">
            <div className="bg-slate-900 h-2 rounded-full transition-all duration-300" style={{
            width: `${currentStep / STEPS.length * 100}%`
          }} data-id="element-1082" data-mp="1li5sb0wkzm5a"></div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col" data-id="element-1083" data-mp="1v9s00n9pn33a">
          <div className="flex-1 p-6 md:p-10 overflow-y-auto" data-id="element-1084" data-mp="1q9xburhbkrlx">
            {renderStepContent()}
          </div>

          {/* Footer Actions */}
          {currentStep < 9 && <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center mt-auto" data-id="element-1085" data-mp="xkqalkqhdnwz">
              <button onClick={handleBack} disabled={currentStep === 1} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1086" data-mp="1bl13etbcpcks">
                Back
              </button>
              <button onClick={handleNext} className="px-6 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-1087" data-mp="2izw1anzfeqr4">
                {currentStep === 8 ? 'Launch Assessment' : 'Continue'}
                {currentStep !== 8 && <ChevronRight className="h-4 w-4 ml-1" data-id="element-1088" data-mp="3s77sz302i3tj" />}
              </button>
            </div>}
        </div>
      </div>
    </div>;
}